import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

function Fallback() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <h1 className="text-title3 font-semibold text-text">{t('errorBoundary.title')}</h1>
      <p className="max-w-prose text-subhead text-text-secondary">{t('errorBoundary.description')}</p>
      <button
        type="button"
        className="rounded-md bg-brand px-4 py-2 text-subhead font-medium text-text-on-brand"
        onClick={() => {
          window.location.reload();
        }}
      >
        {t('errorBoundary.reload')}
      </button>
    </div>
  );
}

/**
 * Last line of defence for failures no use case expected (a render error, a bug).
 * Everything the gateway can answer travels as an `AppError` instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled error in the webmail', error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? <Fallback /> : this.props.children;
  }
}
